import React, { useState } from 'react';
import { Brain, Heart, Wind, Eye, Scan, Utensils, Network, Search, Watch, Camera, Activity, Moon, Sparkles } from 'lucide-react';
import ProjectCard from '../components/ProjectCard';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import MorphingGradient from '../components/MorphingGradient';
import AmbientBackground from '../components/AmbientBackground';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';

const Projects = () => {
  const { t } = useLanguage();
  const [filter, setFilter] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const projects = [
    {
      id: 'cardio',
      title: t('projects.cardio.title'),
      description: t('projects.cardio.description'),
      status: t('projects.cardio.status'),
      icon: Heart,
      color: '#14B8A6',
      path: '/projects/cardio',
    },
    {
      id: 'neuro',
      title: t('projects.neuro.title'),
      description: t('projects.neuro.description'),
      status: t('projects.neuro.status'),
      icon: Brain,
      color: '#7C3AED',
      path: '/projects/neuro',
    },
    {
      id: 'derma',
      title: t('projects.derma.title'),
      description: t('projects.derma.description'),
      status: t('projects.derma.status'),
      icon: Scan,
      color: '#EC4899',
      path: '/projects/derma',
    },
    {
      id: 'pulmo',
      title: t('projects.pulmo.title'),
      description: t('projects.pulmo.description'),
      status: t('projects.pulmo.status'),
      icon: Wind,
      color: '#3B82F6',
      path: '/projects/pulmo',
    },
    {
      id: 'vision',
      title: t('projects.vision.title'),
      description: t('projects.vision.description'),
      status: t('projects.vision.status'),
      icon: Eye,
      color: '#8B5CF6',
      path: '/projects/vision',
    },
    {
      id: 'nutriscan',
      title: t('projects.nutriscan.title'),
      description: t('projects.nutriscan.description'),
      status: t('projects.nutriscan.status'),
      icon: Utensils,
      color: '#10B981',
      path: '/projects/nutriscan',
    },
    {
      id: 'healthnet',
      title: t('projects.healthnet.title'),
      description: t('projects.healthnet.description'),
      status: t('projects.healthnet.status'),
      icon: Network,
      color: '#F59E0B',
      path: '/projects/healthnet',
    },
    {
      id: 'elderband',
      title: t('projects.elderband.title'),
      description: t('projects.elderband.description'),
      status: t('projects.elderband.status'),
      icon: Watch,
      color: '#06B6D4',
      path: '/projects/elderband',
    },
    {
      id: 'fallcam',
      title: t('projects.fallcam.title'),
      description: t('projects.fallcam.description'),
      status: t('projects.fallcam.status'),
      icon: Camera,
      color: '#F97316',
      path: '/projects/fallcam',
    },
    {
      id: 'heartpatch',
      title: t('projects.heartpatch.title'),
      description: t('projects.heartpatch.description'),
      status: t('projects.heartpatch.status'),
      icon: Activity,
      color: '#EF4444',
      path: '/projects/heartpatch',
    },
    {
      id: 'sleepband',
      title: t('projects.sleepband.title'),
      description: t('projects.sleepband.description'),
      status: t('projects.sleepband.status'),
      icon: Moon,
      color: '#6366F1',
      path: '/projects/sleepband',
    },
    {
      id: 'skincam',
      title: t('projects.skincam.title'),
      description: t('projects.skincam.description'),
      status: t('projects.skincam.status'),
      icon: Sparkles,
      color: '#D946EF',
      path: '/projects/skincam',
    },
  ];

  const filters = [t('projects.filterAll'), t('projects.filterRnD'), 'In Validation', 'Commercial', t('projects.filterVision')];

  const filteredProjects = projects.filter(project => {
    const matchesFilter = filter === 'All' || project.status === filter;
    const matchesSearch = project.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         project.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="min-h-screen relative pt-32 pb-24">
      <SEO 
        title={t('projects.title')}
        description="Explore Yamai's seven specialized medical AI projects: Cardio, Neuro, Derma, Pulmo, Vision, NutriScan, and HealthNet. Each targeting specific organ systems with explainable AI."
        keywords="medical AI projects, cardiology AI, neurology AI, dermatology AI, pulmonology AI, ophthalmology AI, nutrition AI, integrated healthcare"
      />

      {/* NEW: Morphing Gradient Background */}
      <MorphingGradient />
      
      {/* NEW: Ambient Background Motion */}
      <AmbientBackground />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="text-gradient-animate">
              {t('projects.title')}
            </span>
          </h1>
          <p className="text-[#8A90A0] text-xl max-w-3xl mx-auto leading-relaxed">
            {t('projects.subtitle')}
          </p>
        </motion.div>

        {/* Search and Filters */}
        <div className="mb-12 space-y-6">
          {/* Search Bar */}
          <div className="relative max-w-xl mx-auto">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-[#8A90A0]" size={20} />
            <Input
              type="text"
              placeholder={t('projects.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 py-6 glass-effect border-[#2B3544] text-white placeholder:text-[#8A90A0] focus:border-yamai-cyan-500 rounded-xl"
            />
          </div>

          {/* Filter Buttons */}
          <div className="flex flex-wrap justify-center gap-3">
            {filters.map((filterOption) => (
              <Button
                key={filterOption}
                onClick={() => setFilter(filterOption)}
                variant={filter === filterOption ? 'default' : 'outline'}
                className={`rounded-xl transition-all duration-300 ${
                  filter === filterOption
                    ? 'bg-gradient-to-r from-yamai-cyan-500 to-yamai-purple-500 hover-glow-cyan text-white'
                    : 'glass-effect border-[#2B3544] text-[#8A90A0] hover:border-yamai-cyan-500 hover:text-white'
                }`}
              >
                {filterOption}
              </Button>
            ))}
          </div>
        </div>

        {/* Projects Grid */}
        {filteredProjects.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, index) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <ProjectCard project={project} />
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-[#8A90A0] text-lg">No projects found matching your criteria.</p>
          </div>
        )}

        {/* Stats Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-24 grid md:grid-cols-4 gap-8"
        >
          <div className="glass-effect rounded-xl p-6 border border-[#2B3544] text-center hover-glow-cyan transition-all duration-300">
            <div className="text-4xl font-bold text-yamai-cyan-400 mb-2">7</div>
            <div className="text-[#8A90A0] text-sm">Active Projects</div>
          </div>
          <div className="glass-effect rounded-xl p-6 border border-[#2B3544] text-center hover-glow-cyan transition-all duration-300">
            <div className="text-4xl font-bold text-yamai-cyan-400 mb-2">6</div>
            <div className="text-[#8A90A0] text-sm">Organ Systems</div>
          </div>
          <div className="glass-effect rounded-xl p-6 border border-[#2B3544] text-center hover-glow-purple transition-all duration-300">
            <div className="text-4xl font-bold text-yamai-purple-400 mb-2">2025</div>
            <div className="text-[#8A90A0] text-sm">Founded</div>
          </div>
          <div className="glass-effect rounded-xl p-6 border border-[#2B3544] text-center hover-glow-purple transition-all duration-300">
            <div className="text-4xl font-bold text-yamai-purple-400 mb-2">2035</div>
            <div className="text-[#8A90A0] text-sm">HealthNet Vision</div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Projects;